import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  communicationApi,
  type AudienceFilters,
  type CreateCampaignBody,
} from '../api/communicationApi';
import { humanizeError } from '@/lib/apiClient';
import { toast } from '@/hooks/useToast';
import type { CampaignStatus } from '../types';

export const commKeys = {
  all: ['communication'] as const,
  templates: (includeInactive: boolean) =>
    [...commKeys.all, 'templates', includeInactive] as const,
  template: (id: string) => [...commKeys.all, 'templates', 'detail', id] as const,
  messages: (limit?: number) => [...commKeys.all, 'messages', limit ?? null] as const,
  message: (id: string) => [...commKeys.all, 'messages', 'detail', id] as const,
  campaigns: (params: { limit?: number; status?: CampaignStatus }) =>
    [...commKeys.all, 'campaigns', params.limit ?? null, params.status ?? null] as const,
  campaign: (id: string) => [...commKeys.all, 'campaigns', 'detail', id] as const,
};

export function useTemplates(includeInactive = false) {
  return useQuery({
    queryKey: commKeys.templates(includeInactive),
    queryFn: () => communicationApi.listTemplates(includeInactive),
    staleTime: 60_000,
  });
}

export function useMessages(limit = 50) {
  return useQuery({
    queryKey: commKeys.messages(limit),
    queryFn: () => communicationApi.listMessages({ limit }),
    staleTime: 30_000,
  });
}

export function useCampaigns(params: { limit?: number; status?: CampaignStatus } = {}) {
  return useQuery({
    queryKey: commKeys.campaigns(params),
    queryFn: () => communicationApi.listCampaigns(params),
    staleTime: 30_000,
  });
}

export function useCampaign(campaignId: string | undefined) {
  return useQuery({
    queryKey: commKeys.campaign(campaignId ?? ''),
    queryFn: () => communicationApi.getCampaign(campaignId as string),
    enabled: !!campaignId,
  });
}

export function useSendCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) => communicationApi.sendCampaign(campaignId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commKeys.all });
      toast({ title: 'Campaign sent', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Send failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useCancelCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) => communicationApi.cancelCampaign(campaignId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commKeys.all });
      toast({ title: 'Campaign cancelled', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Cancel failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCampaignBody) => communicationApi.createCampaign(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commKeys.all });
      toast({ title: 'Campaign created', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Create failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function usePreviewAudience() {
  return useMutation({
    mutationFn: (filters: AudienceFilters) => communicationApi.previewAudience(filters),
    onError: (err) =>
      toast({ title: 'Preview failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useTestSendCampaign() {
  return useMutation({
    mutationFn: ({ campaignId, testUserIds }: { campaignId: string; testUserIds: string[] }) =>
      communicationApi.testSendCampaign(campaignId, testUserIds),
    onSuccess: () => toast({ title: 'Test send dispatched', variant: 'success' }),
    onError: (err) =>
      toast({ title: 'Test send failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => communicationApi.deleteTemplate(templateId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commKeys.all });
      toast({ title: 'Template deactivated', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Delete failed', description: humanizeError(err), variant: 'destructive' }),
  });
}
